import { t as esm_default } from "./esm-WUB3nKhz.js";
import { z } from "zod/v4";
//#region src/zod-to-json.ts
const PATCHED = Symbol("__mastra_patched__");
/**
* Recursively patch Zod v4 record schemas that are missing valueType.
* This fixes a bug in Zod v4 where z.record(valueSchema) doesn't set def.valueType.
* The single-arg form should set valueType but instead only sets keyType.
*
* Idempotent — marks patched schemas with a Symbol so repeat calls no-op.
*
* @internal Exported so the Zod v4 standard-schema adapter can apply the
* same patch before its native `toJSONSchema` call (the legacy `zodToJsonSchema`
* entry already calls it; the `applyCompatLayer` path otherwise wouldn't).
* Not part of the public API.
*/
function patchRecordSchemas(schema) {
	if (!schema || typeof schema !== "object") return schema;
	if (schema[PATCHED]) return schema;
	schema[PATCHED] = true;
	const def = schema._zod?.def;
	if (def?.type === "record" && def.keyType && !def.valueType) {
		def.valueType = def.keyType;
		def.keyType = z.string();
	}
	if (!def) return schema;
	if (def.type === "object" && def.shape) {
		const shape = typeof def.shape === "function" ? def.shape() : def.shape;
		for (const key of Object.keys(shape)) patchRecordSchemas(shape[key]);
	}
	if (def.type === "array" && def.element) patchRecordSchemas(def.element);
	if (def.type === "union" && def.options) def.options.forEach(patchRecordSchemas);
	if (def.type === "record") {
		if (def.keyType) patchRecordSchemas(def.keyType);
		if (def.valueType) patchRecordSchemas(def.valueType);
	}
	if (def.type === "intersection") {
		if (def.left) patchRecordSchemas(def.left);
		if (def.right) patchRecordSchemas(def.right);
	}
	if (def.type === "lazy") {
		if (def.getter && typeof def.getter === "function") {
			const originalGetter = def.getter;
			def.getter = function() {
				const innerSchema = originalGetter();
				if (innerSchema) patchRecordSchemas(innerSchema);
				return innerSchema;
			};
		}
	}
	if (def.innerType) patchRecordSchemas(def.innerType);
	return schema;
}
/**
* Recursively fixes anyOf patterns that some providers (like OpenAI) don't accept.
* Converts anyOf: [{type: X}, {type: "null"}] to type: [X, "null"]
* Also fixes empty {} property schemas by converting to a union of primitive types.
*/
function fixAnyOfNullable(schema) {
	if (typeof schema !== "object" || schema === null) return schema;
	const result = { ...schema };
	if (result.anyOf && Array.isArray(result.anyOf) && result.anyOf.length === 2) {
		const nullSchema = result.anyOf.find((s) => typeof s === "object" && s !== null && s.type === "null");
		const otherSchema = result.anyOf.find((s) => typeof s === "object" && s !== null && s.type !== "null");
		if (nullSchema && otherSchema && typeof otherSchema === "object" && otherSchema.type) {
			const { anyOf, ...rest } = result;
			const fixedRest = fixAnyOfNullable(rest);
			const fixedOther = fixAnyOfNullable(otherSchema);
			return {
				...fixedRest,
				...fixedOther,
				type: Array.isArray(fixedOther.type) ? [...fixedOther.type, "null"] : [fixedOther.type, "null"]
			};
		}
	}
	if (result.properties && typeof result.properties === "object" && !Array.isArray(result.properties)) result.properties = Object.fromEntries(Object.entries(result.properties).map(([key, value]) => {
		const propSchema = value;
		if (typeof propSchema === "object" && propSchema !== null && !Array.isArray(propSchema) && Object.keys(propSchema).length === 0) return [key, { type: [
			"string",
			"number",
			"boolean",
			"null"
		] }];
		return [key, fixAnyOfNullable(propSchema)];
	}));
	if (result.items) if (Array.isArray(result.items)) result.items = result.items.map((item) => fixAnyOfNullable(item));
	else result.items = fixAnyOfNullable(result.items);
	if (result.anyOf && Array.isArray(result.anyOf)) result.anyOf = result.anyOf.map((s) => fixAnyOfNullable(s));
	if (result.oneOf && Array.isArray(result.oneOf)) result.oneOf = result.oneOf.map((s) => fixAnyOfNullable(s));
	if (result.allOf && Array.isArray(result.allOf)) result.allOf = result.allOf.map((s) => fixAnyOfNullable(s));
	return result;
}
/**
* Apply a strict-mode pass to every schema hoisted into `$defs`/`definitions`.
* zod-to-json-schema (v3 path, `$refStrategy: 'relative'`) lifts reused subschemas into
* these definition maps and references them via `$ref`, so the referenced schemas must be
* processed with the same pass as inline nodes or their unsupported keywords leak through.
*/
function applyToDefinitions(result, fn) {
	for (const defKey of ["$defs", "definitions"]) {
		const defs = result[defKey];
		if (defs && typeof defs === "object" && !Array.isArray(defs)) result[defKey] = Object.fromEntries(Object.entries(defs).map(([key, value]) => [key, typeof value === "object" && value !== null ? fn(value) : value]));
	}
}
/**
* Recursively ensures all properties in an object schema are included in the `required` array.
* OpenAI's strict structured output mode requires every key in `properties` to also appear in `required`.
*
* @param schema - The JSON Schema to process
* @returns A new schema with all properties marked as required
*/
function ensureAllPropertiesRequired(schema) {
	if (typeof schema !== "object" || schema === null) return schema;
	const result = { ...schema };
	if (result.type === "object" && result.properties) {
		result.required = Object.keys(result.properties);
		result.properties = Object.fromEntries(Object.entries(result.properties).map(([key, value]) => [key, ensureAllPropertiesRequired(value)]));
	}
	if (result.items) {
		if (Array.isArray(result.items)) result.items = result.items.map((item) => ensureAllPropertiesRequired(item));
		else if (typeof result.items === "object") result.items = ensureAllPropertiesRequired(result.items);
	}
	if (result.additionalProperties && typeof result.additionalProperties === "object") result.additionalProperties = ensureAllPropertiesRequired(result.additionalProperties);
	if (result.anyOf && Array.isArray(result.anyOf)) result.anyOf = result.anyOf.map((s) => ensureAllPropertiesRequired(s));
	if (result.oneOf && Array.isArray(result.oneOf)) result.oneOf = result.oneOf.map((s) => ensureAllPropertiesRequired(s));
	if (result.allOf && Array.isArray(result.allOf)) result.allOf = result.allOf.map((s) => ensureAllPropertiesRequired(s));
	applyToDefinitions(result, ensureAllPropertiesRequired);
	return result;
}
/**
* Prepare a JSON Schema for OpenAI strict mode by ensuring all object properties
* are required and all objects have additionalProperties: false.
*/
function prepareJsonSchemaForOpenAIStrictMode(schema) {
	return stripUnsupportedStrictModeKeywords(ensureAdditionalPropertiesFalse(ensureAllPropertiesRequired(schema)));
}
const STRICT_MODE_DROPPED_KEYWORDS = [
	"contains",
	"minContains",
	"maxContains",
	"minProperties",
	"maxProperties",
	"patternProperties",
	"unevaluatedItems",
	"unevaluatedProperties",
	"not",
	"if",
	"then",
	"else",
	"dependentRequired",
	"dependentSchemas"
];
function isDeepEqual(a, b) {
	if (a === b) return true;
	if (Array.isArray(a) || Array.isArray(b)) return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((v, i) => isDeepEqual(v, b[i]));
	if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) return false;
	const keysA = Object.keys(a);
	const keysB = Object.keys(b);
	return keysA.length === keysB.length && keysA.every((k) => Object.hasOwn(b, k) && isDeepEqual(a[k], b[k]));
}
/**
* Merge `allOf` subschemas into the containing node. OpenAI strict mode rejects `allOf`
* (only `anyOf` composition is supported), so the intersection is flattened into a single
* node. Every keyword from every branch is hoisted so nothing (e.g. `enum`, `const`,
* `items`, `anyOf`) is silently discarded:
*
* - `properties` are unioned; a property present in more than one branch must be identical.
* - `required` is unioned. `additionalProperties: false` wins. Descriptions are concatenated.
* - Any other keyword must either be absent from the target or deep-equal to the branch value.
*
* Intersections that cannot be represented this way (conflicting property schemas, types,
* enums, nested `anyOf`, ...) are rejected with an error instead of producing a schema that
* accepts values the original would have rejected. This mirrors the tool-path behaviour in
* SchemaCompatLayer ("Cannot flatten intersections with overlapping keys").
*/
function mergeAllOfSubschemas(target, subschemas) {
	const mergedProps = { ...target.properties ?? {} };
	const requiredSet = new Set(Array.isArray(target.required) ? target.required : []);
	const descriptions = typeof target.description === "string" && target.description ? [target.description] : [];
	for (const sub of subschemas) {
		if (!sub || typeof sub !== "object") continue;
		for (const [key, value] of Object.entries(sub)) switch (key) {
			case "properties":
				for (const [propName, propSchema] of Object.entries(value)) {
					if (propName in mergedProps && !isDeepEqual(mergedProps[propName], propSchema)) throw new Error(`Cannot flatten allOf for OpenAI strict mode: property "${propName}" is defined differently in multiple branches`);
					mergedProps[propName] = propSchema;
				}
				break;
			case "required":
				if (Array.isArray(value)) for (const name of value) requiredSet.add(name);
				break;
			case "additionalProperties":
				if (value === false) target.additionalProperties = false;
				break;
			case "description":
				if (typeof value === "string" && value) descriptions.push(value);
				break;
			default: if (target[key] === void 0) target[key] = value;
			else if (!isDeepEqual(target[key], value)) throw new Error(`Cannot flatten allOf for OpenAI strict mode: conflicting "${key}" values across branches`);
		}
	}
	if (Object.keys(mergedProps).length) target.properties = mergedProps;
	if (requiredSet.size) target.required = [...requiredSet];
	if (descriptions.length) target.description = descriptions.join(" ");
}
/**
* Merge constraint phrases into a schema node's description.
* Mirrors SchemaCompatLayer.mergeParameterDescription phrasing so the strict-mode
* output path degrades identically to the tool path.
*/
function appendConstraintsToDescription(description, constraints) {
	if (constraints.length === 0) return description;
	const suffix = constraints.join(", ");
	return description ? `${description} (${suffix})` : suffix;
}
/**
* Recursively remove JSON Schema validation keywords that OpenAI strict mode rejects.
* Constraints with useful intent (length/number/pattern/format/uniqueness bounds) are folded
* into the node's `description` — matching how the tool-path SchemaCompatLayer degrades them
* in schema-compatibility.ts — while purely structural unsupported keywords are dropped.
*/
function stripUnsupportedStrictModeKeywords(schema) {
	if (typeof schema !== "object" || schema === null) return schema;
	const result = { ...schema };
	const constraints = [];
	const { minItems, maxItems } = result;
	if (minItems !== void 0 && maxItems !== void 0 && minItems === maxItems) constraints.push(`exact length ${minItems}`);
	else {
		if (minItems !== void 0) constraints.push(`minimum length ${minItems}`);
		if (maxItems !== void 0) constraints.push(`maximum length ${maxItems}`);
	}
	delete result.minItems;
	delete result.maxItems;
	if (result.uniqueItems === true) constraints.push("all items must be unique");
	delete result.uniqueItems;
	if (result.minLength !== void 0) {
		constraints.push(`minimum length ${result.minLength}`);
		delete result.minLength;
	}
	if (result.maxLength !== void 0) {
		constraints.push(`maximum length ${result.maxLength}`);
		delete result.maxLength;
	}
	if (result.format !== void 0) {
		constraints.push(`a valid ${result.format}`);
		delete result.format;
	}
	if (result.pattern !== void 0) {
		constraints.push(`input must match this regex ${result.pattern}`);
		delete result.pattern;
	}
	if (result.minimum !== void 0) {
		if (result.minimum !== Number.MIN_SAFE_INTEGER) constraints.push(`greater than or equal to ${result.minimum}`);
		delete result.minimum;
	}
	if (result.maximum !== void 0) {
		if (result.maximum !== Number.MAX_SAFE_INTEGER) constraints.push(`lower than or equal to ${result.maximum}`);
		delete result.maximum;
	}
	if (result.exclusiveMinimum !== void 0) {
		constraints.push(`greater than ${result.exclusiveMinimum}`);
		delete result.exclusiveMinimum;
	}
	if (result.exclusiveMaximum !== void 0) {
		constraints.push(`lower than ${result.exclusiveMaximum}`);
		delete result.exclusiveMaximum;
	}
	if (result.multipleOf !== void 0) {
		constraints.push(`multiple of ${result.multipleOf}`);
		delete result.multipleOf;
	}
	for (const keyword of STRICT_MODE_DROPPED_KEYWORDS) delete result[keyword];
	if (constraints.length) result.description = appendConstraintsToDescription(result.description, constraints);
	if (result.properties) result.properties = Object.fromEntries(Object.entries(result.properties).map(([key, value]) => [key, stripUnsupportedStrictModeKeywords(value)]));
	if (result.items) {
		if (Array.isArray(result.items)) result.items = result.items.map((item) => stripUnsupportedStrictModeKeywords(item));
		else if (typeof result.items === "object") result.items = stripUnsupportedStrictModeKeywords(result.items);
	}
	if (result.additionalProperties && typeof result.additionalProperties === "object") result.additionalProperties = stripUnsupportedStrictModeKeywords(result.additionalProperties);
	if (result.anyOf && Array.isArray(result.anyOf)) result.anyOf = result.anyOf.map((s) => stripUnsupportedStrictModeKeywords(s));
	if (result.oneOf && Array.isArray(result.oneOf)) {
		if (Array.isArray(result.anyOf)) throw new Error("Cannot convert schema for OpenAI strict mode: \"oneOf\" and \"anyOf\" on the same node cannot be merged without changing semantics");
		result.anyOf = result.oneOf.map((s) => stripUnsupportedStrictModeKeywords(s));
		delete result.oneOf;
	}
	if (result.allOf && Array.isArray(result.allOf)) {
		const merged = result.allOf.map((s) => stripUnsupportedStrictModeKeywords(s));
		delete result.allOf;
		mergeAllOfSubschemas(result, merged);
	}
	applyToDefinitions(result, stripUnsupportedStrictModeKeywords);
	return result;
}
function ensureAdditionalPropertiesFalse(schema) {
	if (typeof schema !== "object" || schema === null) return schema;
	const result = { ...schema };
	if (result.type === "object" || result.properties) result.additionalProperties = false;
	if (result.properties) result.properties = Object.fromEntries(Object.entries(result.properties).map(([key, value]) => [key, ensureAdditionalPropertiesFalse(value)]));
	if (result.items) {
		if (Array.isArray(result.items)) result.items = result.items.map((item) => ensureAdditionalPropertiesFalse(item));
		else if (typeof result.items === "object") result.items = ensureAdditionalPropertiesFalse(result.items);
	}
	if (result.anyOf && Array.isArray(result.anyOf)) result.anyOf = result.anyOf.map((s) => ensureAdditionalPropertiesFalse(s));
	if (result.oneOf && Array.isArray(result.oneOf)) result.oneOf = result.oneOf.map((s) => ensureAdditionalPropertiesFalse(s));
	if (result.allOf && Array.isArray(result.allOf)) result.allOf = result.allOf.map((s) => ensureAdditionalPropertiesFalse(s));
	applyToDefinitions(result, ensureAdditionalPropertiesFalse);
	return result;
}
function zodToJsonSchema(zodSchema, target = "jsonSchema7", strategy = "relative") {
	if (zodSchema?._zod) {
		patchRecordSchemas(zodSchema);
		return fixAnyOfNullable(z.toJSONSchema(zodSchema, {
			unrepresentable: "any",
			io: "input",
			override: (ctx) => {
				const def = ctx.zodSchema?._def || ctx.zodSchema?._zod?.def;
				if (def && (def.typeName === "ZodDate" || def.type === "date")) {
					ctx.jsonSchema.type = "string";
					ctx.jsonSchema.format = "date-time";
				}
				if (def && (def.typeName === "ZodObject" || def.type === "object")) ctx.jsonSchema.additionalProperties = false;
			}
		}));
	} else return esm_default(zodSchema, {
		$refStrategy: strategy,
		target
	});
}
//#endregion
export { ensureAllPropertiesRequired, patchRecordSchemas, prepareJsonSchemaForOpenAIStrictMode, zodToJsonSchema };

//# sourceMappingURL=zod-to-json.js.map